import React, { useCallback } from "react";
import { useHistory } from "react-router-dom";
import { MODE } from "../../config/consts";
import {
  AtRiskSection,
  BannerSection,
  IntroSection,
  SceneAnswerProps,
  TitleProps,
  UserAnswerProps,
  TextImageProps,
  TextAndFlipCardProps,
  TitleLinkProps,
} from "./types";
import {
  AnswerText,
  BannerWrapper,
  ComponentWrapper,
  Container,
  DebriefAtRiskWrapper,
  FlexContainer,
  FlipCardsContainer,
  Intro,
  ReplayButton,
  ReplayScene,
  ReplaySceneWrapper,
  Spacer,
  TextAndFlipCardContainer,
  TextAndImageCardContainer,
  TitleLinkWrapper,
  TitleWrapper,
  TitleSize,
  UserAnswerWrapper,
  UserContainer,
} from "./styles";
import { useApp, UserAnswerType } from "../../state/app";
import scorm from "../../services/scorm";
import env from "../../env";
import useAppLogic from "../../hooks/use-app-logic";
import FlipCard from "../flip-card/flip-card";

export function IntroComponent({ backgroundColor, text }: IntroSection) {
  const { language } = useAppLogic();
  return (
    <Container>
      <ComponentWrapper $backgroundColor={backgroundColor}>
        <Intro dangerouslySetInnerHTML={{ __html: text?.[language] ?? "" }} />
      </ComponentWrapper>
    </Container>
  );
}

export function BannerComponent({
  backgroundColor,
  image = "",
  title,
  titleColor = "",
}: BannerSection) {
  const { language } = useAppLogic();
  return (
    <BannerWrapper imgSrc={image} variant={!title ? "primary" : "secondary"}>
      {title && (
        <Container>
          <ComponentWrapper $backgroundColor={backgroundColor}>
            {title?.[language] && (
              <h1 style={{ color: titleColor ?? undefined }}>
                {title?.[language] ?? ""}
              </h1>
            )}
          </ComponentWrapper>
        </Container>
      )}
    </BannerWrapper>
  );
}

export function AtRiskComponent({
  backgroundColor,
  image = "",
  quote,
  title,
  titleColor = "",
}: AtRiskSection) {
  const { language } = useAppLogic();
  return (
    <DebriefAtRiskWrapper>
      <Container>
        <ComponentWrapper $backgroundColor={backgroundColor}>
          <h2 style={{ color: titleColor }}>{title?.[language] ?? ""}</h2>
          <div className="row row--1fr-2fr">
            <div className="col">
              <div className="img-card">
                <img src={image} alt={""} />
              </div>
            </div>
            <div
              className="col"
              dangerouslySetInnerHTML={{ __html: quote?.[language] ?? "" }}
            />
          </div>
        </ComponentWrapper>
      </Container>
    </DebriefAtRiskWrapper>
  );
}

export function Quote({
  backgroundColor,
  image = "",
  quote,
  title,
  titleColor = "",
}: AtRiskSection) {
  const { language } = useAppLogic();
  return (
    <DebriefAtRiskWrapper>
      <Container>
        <ComponentWrapper $backgroundColor={backgroundColor}>
          <h2 style={{ color: titleColor }}>{title?.[language] ?? ""}</h2>
          <div className="row row--1fr-2fr">
            <div className="col">
              <div className="img-card">
                <img src={image} alt={"Debrief"} />
              </div>
              <div className="group-quote">
                <img
                  className="quote"
                  src="https://e-learning-cms.s3.eu-west-2.amazonaws.com/e-learning-cms/uploads/quote.svg"
                  alt="quote"
                />
                <img
                  className="quote"
                  src="https://e-learning-cms.s3.eu-west-2.amazonaws.com/e-learning-cms/uploads/quote.svg"
                  alt="quote"
                />
              </div>
            </div>
            <div
              className="col"
              dangerouslySetInnerHTML={{ __html: quote?.[language] ?? "" }}
            />
          </div>
        </ComponentWrapper>
      </Container>
    </DebriefAtRiskWrapper>
  );
}

export function Title({ backgroundColor, text }: TitleProps) {
  const { language } = useAppLogic();
  return (
    <TitleWrapper>
      <Container>
        <ComponentWrapper $backgroundColor={backgroundColor}>
          <h1>{text?.[language] ?? ""}</h1>
        </ComponentWrapper>
      </Container>
    </TitleWrapper>
  );
}

export function UserAnswer({
  backgroundColor,
  textColor,
  fullReplay = false,
  text,
  sceneIds,
  image = "https://e-learning-cms.s3.eu-west-2.amazonaws.com/e-learning-cms/uploads/humility_thumbnail.jpg",
  debriefSceneId,
  currentSectionIndex,
}: UserAnswerProps) {
  const { userAnswer, setDebriefId, setDebriefIndex, client, setDebriefMode } =
    useApp();
  const history = useHistory();
  const { language, appText } = useAppLogic();

  const handleRedirectUser = useCallback(
    (sceneId) => {
      console.log("handleRedirectUser", { sceneId });
      if (sceneId) {
        console.log("handleRedirectUser", { fullReplay });
        setDebriefMode(fullReplay ? "full" : "partial");
        setDebriefId(debriefSceneId);
        setDebriefIndex(currentSectionIndex);
        history.push(sceneId);
      }
    },
    [
      fullReplay,
      history,
      setDebriefId,
      debriefSceneId,
      currentSectionIndex,
      setDebriefIndex,
    ],
  );

  function getSceneAnswer(
    userAnswer: UserAnswerType,
    sceneIds: string[],
  ): null | SceneAnswerProps {
    if (!sceneIds) {
      return null;
    }
    for (const sceneId in userAnswer) {
      if (!userAnswer.hasOwnProperty(sceneId)) {
        continue;
      }
      if (sceneIds.includes(sceneId)) {
        return { sceneAnswer: userAnswer[sceneId], sceneId: sceneId };
      }
    }

    return null;
  }

  const getAnswer = getSceneAnswer(userAnswer, sceneIds);
  const paragraphs = (text?.[language] ?? "").split(/<p>(.*?)<\/p>/);
  let lastIndexWithContent = -1;
  for (let index = 0; index < paragraphs.length; index++) {
    const paragraph = paragraphs[index].trim();
    if (!["\n", "&nbsp;", ""].includes(paragraph)) {
      lastIndexWithContent = index;
    }
  }
  const userAnswerText = paragraphs
    .map((paragraph, index) => {
      if (index === lastIndexWithContent) {
        paragraph = `${paragraph} <strong>${
          getAnswer?.sceneAnswer ?? ""
        }</strong>`;
      }
      return `<p>${paragraph}</p>`;
    })
    .join("");
  return (
    <UserAnswerWrapper>
      <Container>
        <ComponentWrapper $backgroundColor={backgroundColor}>
          <UserContainer>
            <AnswerText
              dangerouslySetInnerHTML={{ __html: userAnswerText }}
              client={client.name}
              $textColor={textColor}
            />
            <ReplaySceneWrapper>
              <ReplayScene>
                <Spacer
                  backgroundImage={
                    image ??
                    "https://e-learning-cms.s3.eu-west-2.amazonaws.com/e-learning-cms/uploads/humility_thumbnail.jpg"
                  }
                />
                <ReplayButton
                  onClick={() => handleRedirectUser(getAnswer?.sceneId)}
                  aria-label={
                    appText?.replayScene?.[language] ?? "Replay decision"
                  }
                >
                  <span>
                    {appText?.replayScene?.[language] ?? "Replay decision"}
                  </span>
                  <span></span>
                </ReplayButton>
              </ReplayScene>
            </ReplaySceneWrapper>
          </UserContainer>
        </ComponentWrapper>
      </Container>
    </UserAnswerWrapper>
  );
}

export function Replay({
  backgroundColor,
  text,
  image = "https://e-learning-cms.s3.eu-west-2.amazonaws.com/e-learning-cms/uploads/humility_thumbnail.jpg",
}: UserAnswerProps) {
  const { projectID, client } = useApp();
  const history = useHistory();
  const { language } = useAppLogic();

  const reset = React.useCallback(() => {
    const { setDebriefMode, setUserAnswer } = useApp.getState();
    setDebriefMode(null);
    setUserAnswer({});
    if (scorm.isConnected()) {
      scorm.setData(null);
      history.push(`/`);
    } else if (MODE.startsWith("web")) {
      localStorage.removeItem(env.localStorage.language);
      localStorage.removeItem(env.localStorage.userAnswer);
      localStorage.removeItem(env.localStorage.languageSelected);
      history.push(`/`);
    } else {
      localStorage.removeItem(env.localStorage.language);
      localStorage.removeItem(env.localStorage.userAnswer);
      localStorage.removeItem(env.localStorage.languageSelected);
      history.push(`/${projectID}`);
    }
  }, [history, projectID]);

  return (
    <UserAnswerWrapper>
      <Container>
        <ComponentWrapper $backgroundColor={backgroundColor}>
          <UserContainer>
            <AnswerText
              dangerouslySetInnerHTML={{
                __html: `${text[language]}`,
              }}
              client={client.name}
            />
            <ReplaySceneWrapper>
              <ReplayScene>
                <Spacer
                  backgroundImage={
                    image ??
                    "https://e-learning-cms.s3.eu-west-2.amazonaws.com/e-learning-cms/uploads/humility_thumbnail.jpg"
                  }
                />
                <ReplayButton onClick={reset}>
                  <span>Replay experience</span>
                  <span></span>
                </ReplayButton>
              </ReplayScene>
            </ReplaySceneWrapper>
          </UserContainer>
        </ComponentWrapper>
      </Container>
    </UserAnswerWrapper>
  );
}

export function TextAndImage(props: TextImageProps) {
  const { language } = useAppLogic();

  return (
    <Container>
      <ComponentWrapper $backgroundColor={props.backgroundColor}>
        <FlexContainer>
          <TextAndImageCardContainer $cardAlignment={props.imgAlignment}>
            <img src={props.image} alt={props.imgAlt?.[language] ?? ""} />

            <div>
              <TitleSize $size={props.titleSize || ""}>
                {props.title?.[language] ?? ""}
              </TitleSize>
              <Intro
                dangerouslySetInnerHTML={{
                  __html: props.text?.[language] ?? "",
                }}
              />
            </div>
          </TextAndImageCardContainer>
        </FlexContainer>
      </ComponentWrapper>
    </Container>
  );
}

function IconFlipCard() {
  return (
    <div>
      <svg
        width="42"
        height="40"
        viewBox="0 0 42 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2.49148 22.5809H4.90107C5.24516 22.5869 5.57742 22.7075 5.84521 22.9236C6.11299 23.1398 6.30098 23.4391 6.37939 23.7742C7.05453 26.229 8.36137 28.4638 10.1696 30.2559C11.9778 32.048 14.2243 33.3348 16.685 33.988C19.1456 34.6411 21.7345 34.6378 24.1935 33.9783C26.6524 33.3188 28.8956 32.0263 30.6992 30.2295L31.5796 29.3478L26.5479 24.3162C26.4533 24.2233 26.3781 24.1124 26.3268 23.9901C26.2754 23.8677 26.249 23.7364 26.249 23.6038C26.2481 23.3431 26.347 23.0919 26.5254 22.9018C26.7037 22.7116 26.948 22.5968 27.2082 22.5809H40.8846C41.0515 22.5822 41.2113 22.6491 41.3293 22.7672C41.4474 22.8852 41.5143 23.045 41.5156 23.2119V36.8883C41.4997 37.1485 41.3849 37.3928 41.1948 37.5712C41.0046 37.7495 40.7535 37.8484 40.4928 37.8476C40.36 37.8475 40.2286 37.8208 40.1063 37.7693C39.9839 37.7177 39.8731 37.6423 39.7803 37.5473L35.1787 32.9457L34.7258 33.3999C32.1816 36.0277 28.9828 37.9297 25.4589 38.9099C22.8916 39.5915 20.2127 39.7451 17.5843 39.3612C14.9559 38.9773 12.4328 38.064 10.1676 36.6766C7.90254 35.2891 5.94257 33.4564 4.40642 31.2894C2.87027 29.1224 1.78989 26.6661 1.23071 24.0694C1.19815 23.886 1.20644 23.6977 1.25496 23.5179C1.30348 23.338 1.39105 23.1711 1.51144 23.029C1.63182 22.8869 1.78205 22.773 1.95145 22.6956C2.12085 22.6181 2.30523 22.579 2.49148 22.5809ZM1.82738 2.22534C1.96014 2.22545 2.09154 2.25207 2.21387 2.30363C2.33621 2.35518 2.44703 2.43064 2.53982 2.52559L7.14145 7.12721L7.59436 6.67303C10.1386 4.04521 13.3374 2.14322 16.8612 1.16304C19.4285 0.481362 22.1075 0.327814 24.7359 0.711688C27.3642 1.09556 29.8874 2.00887 32.1525 3.39632C34.4176 4.78377 36.3776 6.61648 37.9137 8.78351C39.4499 10.9505 40.5302 13.4068 41.0894 16.0035C41.122 16.1869 41.1137 16.3752 41.0652 16.555C41.0167 16.7349 40.9291 16.9018 40.8087 17.0439C40.6883 17.186 40.5381 17.2999 40.3687 17.3773C40.1993 17.4548 40.0149 17.4939 39.8287 17.492H37.4191C37.075 17.4858 36.7429 17.3651 36.4751 17.149C36.2074 16.9329 36.0194 16.6337 35.9407 16.2987C35.2656 13.844 33.9588 11.6091 32.1505 9.81699C30.3423 8.02486 28.0958 6.73807 25.6352 6.08494C23.1745 5.4318 20.5856 5.43513 18.1267 6.0946C15.6677 6.75407 13.4246 8.04664 11.6209 9.84341L10.7406 10.7251L15.7722 15.7567C15.8668 15.8497 15.942 15.9606 15.9933 16.0829C16.0446 16.2052 16.0711 16.3365 16.0712 16.4691C16.0717 16.7298 15.9727 16.9807 15.7944 17.1708C15.6161 17.3609 15.372 17.4758 15.1119 17.492H1.43553C1.26859 17.4907 1.10886 17.4238 0.990807 17.3057C0.872753 17.1877 0.805843 17.0279 0.804512 16.861V3.1846C0.820419 2.92438 0.935207 2.68009 1.12537 2.50175C1.31553 2.32342 1.56668 2.22453 1.82738 2.22534Z"
          fill="black"
        />
      </svg>
    </div>
  );
}

export function TextAndFlipCard(props: TextAndFlipCardProps) {
  const { language } = useAppLogic();
  const [isFlipped, setIsFlipped] = React.useState(true);

  return (
    <Container>
      <ComponentWrapper $backgroundColor={props.backgroundColor}>
        <FlexContainer>
          <TextAndFlipCardContainer
            $cardAlignment={props.cardAlignment}
            role="region"
            aria-label="Rotating Card"
          >
            <FlipCard
              cardFrontText={props.cardSide1Text?.[language] ?? ""}
              cardFrontImg={props.cardSide1Image}
              cardBackText={props.cardSide2Text?.[language] ?? ""}
              cardBackImg={props.cardSide2Image}
              cardFrontBgColor={props.cardSide1BgColor}
              cardBackBgColor={props.cardSide2BgColor}
            />
            <Intro
              dangerouslySetInnerHTML={{ __html: props.text?.[language] ?? "" }}
            />
          </TextAndFlipCardContainer>
        </FlexContainer>
      </ComponentWrapper>
    </Container>
  );
}

export function TitleLink({
  backgroundColor,
  title,
  titleColor,
  linkText,
  linkURL,
  linkBackgroundColor,
  linkTextColor,
}: TitleLinkProps) {
  const { language } = useAppLogic();
  return (
    <TitleLinkWrapper
      $titleColor={titleColor}
      $linkBackgroundColor={linkBackgroundColor}
      $linkTextColor={linkTextColor}
    >
      <Container>
        <ComponentWrapper $backgroundColor={backgroundColor} $noPadding>
          <p>{title?.[language] ?? ""}</p>
          <a
            href={linkURL?.["en"] ?? ""}
            target="_blank"
            rel="noopener noreferrer"
          >
            {linkText?.[language] ?? ""}
          </a>
        </ComponentWrapper>
      </Container>
    </TitleLinkWrapper>
  );
}

const CARD_PREFIX = "card";
export function FlipCards(props: any) {
  const { language } = useAppLogic();

  const cardsPrefix = Object.keys(props).reduce((acc: string[], keyValue) => {
    if (keyValue.startsWith(CARD_PREFIX)) {
      const prefix = keyValue.split("_")[0];
      if (!acc.includes(prefix)) {
        acc.push(prefix);
      }
    }

    return acc;
  }, []);

  const cards = cardsPrefix.map((prefix) => ({
    frontText: props[`${prefix}_frontText`],
    frontBgColor: props[`${prefix}_frontBgColor`],
    frontImage: props[`${prefix}_frontImage`],
    backText: props[`${prefix}_backText`],
    backBgColor: props[`${prefix}_backBgColor`],
    backImage: props[`${prefix}_backImage`],
  }));

  return (
    <Container>
      <ComponentWrapper>
        <FlipCardsContainer role="region" aria-label="Rotating Cards">
          {cards.length > 0 &&
            cards.map((card) => (
              <FlipCard
                key={card.frontText?.[language]}
                cardFrontText={card.frontText?.[language]}
                cardFrontBgColor={card.frontBgColor}
                cardFrontImg={card.frontImage}
                cardBackText={card.backText?.[language]}
                cardBackBgColor={card.backBgColor}
                cardBackImg={card.backImage}
              />
            ))}
        </FlipCardsContainer>
      </ComponentWrapper>
    </Container>
  );
}
